import type { Model } from "./index.js";

export type FilterableModel = Model & {
  providerEnabled?: boolean;
};

export type ModelAvailabilityFilter = "all" | "available" | "unavailable";

export type ModelFilter = {
  query?: string | null;
  capability?: string | null;
  modality?: string | null;
  providerId?: string | null;
  provider?: string | null;
  tag?: string | null;
  availability?: ModelAvailabilityFilter | null;
  offset?: number;
  limit?: number;
};

const capabilityAliases = new Map([
  ["vision", "vision"],
  ["imagegeneration", "imageGeneration"],
  ["image-generation", "imageGeneration"],
  ["tools", "tools"],
  ["text-output", "text-output"],
  ["image-output", "image-output"],
]);

function normalize(value: unknown) {
  return typeof value === "string"
    ? value.normalize("NFKC").trim().toLocaleLowerCase("und")
    : "";
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === "string")
    : [];
}

function object(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function modalityValues(model: FilterableModel) {
  const architecture = object(model.architecture);
  const remote = object(model.remoteMetadata);
  const remoteArchitecture = object(remote?.architecture);
  return {
    input: [
      ...strings(architecture?.input_modalities),
      ...strings(architecture?.inputModalities),
      ...strings(remoteArchitecture?.input_modalities),
      ...strings(remoteArchitecture?.inputModalities),
    ].map(normalize),
    output: [
      ...strings(architecture?.output_modalities),
      ...strings(architecture?.outputModalities),
      ...strings(remoteArchitecture?.output_modalities),
      ...strings(remoteArchitecture?.outputModalities),
    ].map(normalize),
  };
}

/** Whether a model can accept image input, including legacy metadata-only rows. */
export function modelSupportsVision(model: FilterableModel) {
  return (
    model.capabilities.vision || modalityValues(model).input.includes("image")
  );
}

function modalities(model: FilterableModel) {
  const values = modalityValues(model);
  return [...values.input, ...values.output];
}

function capabilityMatches(model: FilterableModel, value: string) {
  const capability = capabilityAliases.get(value);
  if (!capability) return false;
  if (capability === "tools")
    return model.capabilities.tools && model.toolsEnabled;
  if (capability === "text-output") {
    const outputs = modalityValues(model).output;
    return outputs.length === 0 || outputs.includes("text");
  }
  if (capability === "image-output")
    return (
      model.capabilities.imageGeneration ||
      modalityValues(model).output.includes("image")
    );
  if (capability === "vision") return modelSupportsVision(model);
  return Boolean(model.capabilities[capability as keyof Model["capabilities"]]);
}

function isAvailable(model: FilterableModel) {
  return (
    model.enabled !== false &&
    model.providerEnabled !== false &&
    model.userEnabled !== false
  );
}

function searchableText(model: FilterableModel) {
  return [
    model.name,
    model.providerName,
    model.description,
    model.remoteId,
    ...(model.tags ?? []),
    ...modalities(model),
  ]
    .map(normalize)
    .join(" ");
}

/** Apply the exact AND-composed model filter contract used by model UIs. */
export function filterModels<T extends FilterableModel>(
  models: readonly T[],
  filter: ModelFilter = {},
): T[] {
  const query = normalize(filter.query);
  const capability = normalize(filter.capability);
  const modality = normalize(filter.modality);
  const providerId = normalize(filter.providerId);
  const provider = normalize(filter.provider);
  const tag = normalize(filter.tag);
  const availability = filter.availability ?? "all";
  const filtered = models.filter((model) => {
    const tags = (model.tags ?? []).map(normalize);
    const modelModalities = modalities(model);
    const matchesQuery = !query || searchableText(model).includes(query);
    const matchesCapability =
      !capability || capabilityMatches(model, capability);
    const matchesModality = !modality || modelModalities.includes(modality);
    const matchesProviderId =
      !providerId || normalize(model.providerId) === providerId;
    const matchesProvider =
      !provider || normalize(model.providerName) === provider;
    const matchesTag = !tag || tags.includes(tag);
    const available = isAvailable(model);
    const matchesAvailability =
      availability === "all" ||
      (availability === "available" && available) ||
      (availability === "unavailable" && !available);
    return (
      matchesQuery &&
      matchesCapability &&
      matchesModality &&
      matchesProviderId &&
      matchesProvider &&
      matchesTag &&
      matchesAvailability
    );
  });
  const offset = Math.max(
    0,
    Number.isFinite(filter.offset) ? Math.floor(filter.offset ?? 0) : 0,
  );
  if (filter.limit === undefined || filter.limit === null)
    return filtered.slice(offset);
  const rawLimit = Number(filter.limit);
  const limit = Number.isFinite(rawLimit)
    ? Math.min(1_000, Math.max(0, Math.floor(rawLimit)))
    : 0;
  return filtered.slice(offset, offset + limit);
}
